import { api, type Page } from '@/shared/api/client';
import { apiUrl } from '@/shared/config';

import type {
  AccessLogEntry,
  AccessLogFilters,
  AdminUser,
  AdminUserCreate,
  AdminUserCreated,
  AdminUserUpdate,
  ConnectionTest,
  DocumentRetentionPolicy,
  FeedbackFile,
  ErrorEvent,
  ErrorFilters,
  ErrorSummary,
  ReliabilityFilters,
  ReliabilityReport,
  FeedbackAnswerFilters,
  FeedbackAnswers,
  FeedbackFilters,
  FeedbackSummary,
  InviteRead,
  Mailbox,
  MailboxCredentials,
  MailboxList,
  MailboxUpdate,
  MailboxWrite,
  ModesWrite,
  ProfileRead,
  RetentionPreview,
  RetentionRun,
  StorageUsage,
  TriageActivation,
  UserHistory,
} from './types';

export const adminKeys = {
  users: ['admin', 'users'] as const,
  /** Under `users`: every write that invalidates the list reads the history again too. */
  userHistory: (id: string) => ['admin', 'users', id, 'history'] as const,
  profiles: ['admin', 'profiles'] as const,
  mailboxes: ['admin', 'mailboxes'] as const,
  triage: ['admin', 'triage'] as const,
  accessLog: (filters: AccessLogFilters) => ['admin', 'access-log', filters] as const,
  retention: ['admin', 'retention'] as const,
  storage: ['admin', 'storage'] as const,
  documentPolicy: ['admin', 'document-policy'] as const,
  feedback: (filters: FeedbackFilters) => ['admin', 'feedback', filters] as const,
  feedbackMailboxes: ['admin', 'feedback', 'mailboxes'] as const,
  feedbackAnswers: (filters: FeedbackAnswerFilters) =>
    ['admin', 'feedback', 'answers', filters] as const,
  errors: (filters: ErrorFilters) => ['admin', 'errors', filters] as const,
  errorOccurrences: (fingerprint: string, since: string) =>
    ['admin', 'errors', 'occurrences', fingerprint, since] as const,
  reliability: (filters: ReliabilityFilters) => ['admin', 'reliability', filters] as const,
};

/** The download of Administración → Incidencias (contract v10.31): every row of the
 * window, filters included. A plain link, like the other downloads. */
export function errorsFileUrl(filters: ErrorFilters): string {
  const url = new URL(`${apiUrl}/api/admin/errors.jsonl`);
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== '') url.searchParams.set(key, String(value));
  }
  return url.toString();
}

export const ACCESS_LOG_PAGE = 50;
export const ANSWERS_PAGE = 50;

/** The same filters as a query string, for the CSV link: what the person sees is what
 * downloads, and nothing else. */
export function accessLogCsvUrl(filters: AccessLogFilters): string {
  const url = new URL(`${apiUrl}/api/admin/access-log.csv`);
  for (const [key, value] of Object.entries(filters)) {
    if (key !== 'offset' && value !== undefined && value !== '')
      url.searchParams.set(key, String(value));
  }
  return url.toString();
}

/** A download of Administración → Correcciones (contract v10.28): the window of the
 * screen and the one option of each file, as a query string. A plain link, like the
 * access log's CSV: the session cookie goes with it and the browser saves the file. */
export function feedbackFileUrl(
  file: FeedbackFile,
  filters: FeedbackFilters,
  only = false,
): string {
  const url = new URL(`${apiUrl}/api/admin/feedback/${file}`);
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== '') url.searchParams.set(key, String(value));
  }
  if (only)
    url.searchParams.set(file === 'extraction.jsonl' ? 'only_changed' : 'only_corrected', 'true');
  return url.toString();
}

export const adminApi = {
  listUsers: (): Promise<Page<AdminUser>> =>
    api.get<Page<AdminUser>>('/api/admin/users', { limit: 200 }),
  /** With `invite: true` the answer carries the one-time link (contract v10.10). */
  createUser: (payload: AdminUserCreate): Promise<AdminUserCreated> =>
    api.post<AdminUserCreated>('/api/admin/users', payload),
  /** A fresh link for somebody who already exists: lost, expired, or as a way to replace a
   * forgotten password without anybody typing one. */
  inviteUser: (id: string): Promise<InviteRead> =>
    api.post<InviteRead>(`/api/admin/users/${id}/invite`),
  updateUser: (id: string, payload: AdminUserUpdate): Promise<AdminUser> =>
    api.patch<AdminUser>(`/api/admin/users/${id}`, payload),
  resetPassword: (id: string, password: string): Promise<AdminUser> =>
    api.post<AdminUser>(`/api/admin/users/${id}/password`, { password }),
  /** Contract v10.45: their app, codes and remembered devices go, their sessions close, and
   * they set it up again at their next login. Elevated. */
  resetTwoFactor: (id: string): Promise<AdminUser> =>
    api.post<AdminUser>(`/api/admin/users/${id}/two-factor/reset`),
  /** Contract v10.58: who changed what of this person's access. Reads, no elevation. */
  userHistory: (id: string): Promise<UserHistory> =>
    api.get<UserHistory>(`/api/admin/users/${id}/history`),
  /** Contract v10.58: what each profile allows, as the API enforces it. */
  profiles: (): Promise<ProfileRead[]> => api.get<ProfileRead[]>('/api/admin/profiles'),

  listMailboxes: (): Promise<MailboxList> => api.get<MailboxList>('/api/admin/mailboxes'),
  testMailbox: (payload: MailboxCredentials): Promise<ConnectionTest> =>
    api.post<ConnectionTest>('/api/admin/mailboxes/test', payload),
  /** Contract v10.17: with what is stored, over which what `typed` carries is tested. */
  testSavedMailbox: (id: string, typed: MailboxUpdate = {}): Promise<ConnectionTest> =>
    api.post<ConnectionTest>(`/api/admin/mailboxes/${id}/test`, typed),
  createMailbox: (payload: MailboxWrite): Promise<Mailbox> =>
    api.post<Mailbox>('/api/admin/mailboxes', payload),
  updateMailbox: (id: string, payload: MailboxUpdate): Promise<Mailbox> =>
    api.patch<Mailbox>(`/api/admin/mailboxes/${id}`, payload),
  deleteMailbox: (id: string): Promise<void> => api.delete<void>(`/api/admin/mailboxes/${id}`),

  triage: (): Promise<TriageActivation> => api.get<TriageActivation>('/api/admin/triage'),
  putDestinations: (addresses: Record<string, string[]>): Promise<TriageActivation> =>
    api.put<TriageActivation>('/api/admin/triage/destinations', { addresses }),
  putFolders: (folders: Record<string, string>): Promise<TriageActivation> =>
    api.put<TriageActivation>('/api/admin/triage/folders', { folders }),
  putModes: (payload: ModesWrite): Promise<TriageActivation> =>
    api.put<TriageActivation>('/api/admin/triage/modes', payload),
  allShadow: (): Promise<TriageActivation> =>
    api.post<TriageActivation>('/api/admin/triage/all-shadow'),

  accessLog: (filters: AccessLogFilters): Promise<Page<AccessLogEntry>> =>
    api.get<Page<AccessLogEntry>>('/api/admin/access-log', {
      ...filters,
      limit: ACCESS_LOG_PAGE,
      offset: filters.offset ?? 0,
    }),
  retention: (): Promise<RetentionPreview> => api.get<RetentionPreview>('/api/admin/retention'),
  feedbackAnswers: (filters: FeedbackAnswerFilters): Promise<FeedbackAnswers> =>
    api.get<FeedbackAnswers>('/api/admin/feedback/answers', {
      ...filters,
      limit: ANSWERS_PAGE,
      offset: filters.offset ?? 0,
    }),
  feedback: (filters: FeedbackFilters): Promise<FeedbackSummary> =>
    api.get<FeedbackSummary>('/api/admin/feedback', { ...filters }),
  /** The mailboxes that have mail, which is what `emails.mailbox` holds and the filter
   * of the API compares with. */
  feedbackMailboxes: (): Promise<Page<string>> =>
    api.get<Page<string>>('/api/emails/mailboxes', { limit: 200 }),
  errors: (filters: ErrorFilters): Promise<ErrorSummary> =>
    api.get<ErrorSummary>('/api/admin/errors', { ...filters }),
  errorOccurrences: (fingerprint: string, since: string): Promise<ErrorEvent[]> =>
    api.get<ErrorEvent[]>(`/api/admin/errors/${encodeURIComponent(fingerprint)}`, { since }),
  runRetention: (): Promise<RetentionRun> => api.post<RetentionRun>('/api/admin/retention/run'),
  /** Contract v10.49: what the bucket weighed when it was last measured. */
  storage: (): Promise<StorageUsage> => api.get<StorageUsage>('/api/admin/storage'),
  /** Walks the whole bucket: one call to it per thousand files. */
  measureStorage: (): Promise<StorageUsage> => api.post<StorageUsage>('/api/admin/storage/measure'),
  /** Contract v10.37: superadministrators only; reads, changes nothing. */
  reliability: (filters: ReliabilityFilters): Promise<ReliabilityReport> =>
    api.get<ReliabilityReport>('/api/admin/reliability', { ...filters }),
  documentPolicy: (): Promise<DocumentRetentionPolicy> =>
    api.get<DocumentRetentionPolicy>('/api/documents/retention-policy'),
  putDocumentPolicy: (days: Record<string, number>): Promise<DocumentRetentionPolicy> =>
    api.put<DocumentRetentionPolicy>('/api/documents/retention-policy', { days }),
};
